import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

/**
 * Guess Me build.
 * This page keeps a minimal "raw JSON" editor for theme updates.
 */
export default function ThemeUpdate() {
  const dispatch = useDispatch();
  const token = useSelector(selectAdminToken);
  const { data: themeData, status } = useSelector((s) => s.theme);

  const initialText = useMemo(() => {
    try {
      return JSON.stringify(themeData ?? {}, null, 2);
    } catch {
      return "{}";
    }
  }, [themeData]);

  const [jsonText, setJsonText] = useState(initialText);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    const tid = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("currentTheme") : null;
    dispatch(fetchThemeData({ themeId: tid }));
  }, [dispatch, token]);

  useEffect(() => {
    setJsonText(initialText);
  }, [initialText]);

  const handleSave = async () => {
    if (!token) return;
    let parsed;
    try {
      parsed = JSON.parse(jsonText || "{}");
    } catch (e) {
      await Swal.fire("Invalid JSON", e?.message || "Please fix JSON and try again.", "error");
      return;
    }
    setIsSaving(true);
    try {
      await updateThemeData({ token, payload: parsed });
      await Swal.fire("Saved", "Theme updated successfully.", "success");
      dispatch(fetchThemeData({ themeId: null }));
    } catch (e) {
      await Swal.fire("Save failed", e?.message || "Failed to update theme.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 12px" }}>Theme Update</h2>
      <p style={{ margin: "0 0 12px", opacity: 0.85 }}>
        Status: <strong>{status || "idle"}</strong>
      </p>
      <textarea
        value={jsonText}
        onChange={(e) => setJsonText(e.target.value)}
        spellCheck={false}
        style={{
          width: "100%",
          minHeight: 520,
          padding: 12,
          borderRadius: 10,
          border: "1px solid rgba(0,0,0,0.2)",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          fontSize: 13,
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={!token || isSaving}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            background: "#0f43c9",
            color: "#fff",
            fontWeight: 800,
            cursor: !token || isSaving ? "not-allowed" : "pointer",
            opacity: !token || isSaving ? 0.7 : 1,
          }}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

